/**
 * De sturingslagen van het dashboard: strategisch, tactisch en operationeel.
 *
 * Besluit K10 (Tom en Jan, 28-10-2026): naast de rechten heeft een gebruiker
 * een of meer sturingsrollen. Die bepalen welk dashboard iemand ziet; wie
 * alleen operationeel is, komt niet in het strategische dashboard. Met één
 * rol is er geen schakelaar, met meer een subtiele schakelaar rechtsboven,
 * alleen tussen de eigen lagen. Een beheerder heeft altijd alle drie.
 *
 * De backend beslist (`require_layer` op de KPI-routes); dit bestand bepaalt
 * alleen wat het scherm aanbiedt. De rollen komen uit `GET /me`.
 */
import { useAuth } from "@/auth/authState";

/** De laagcode zoals de API hem kent (`services/kpi_registry.py`). */
export type LayerCode = "strategic" | "tactical" | "operational";

export type SteeringLayer = {
  /** Het stuk van de route: `/dashboard/strategisch`. */
  id: "strategisch" | "tactisch" | "operationeel";
  code: LayerCode;
  label: string;
};

export const STEERING_LAYERS: readonly SteeringLayer[] = [
  { id: "strategisch", code: "strategic", label: "Strategisch" },
  { id: "tactisch", code: "tactical", label: "Tactisch" },
  { id: "operationeel", code: "operational", label: "Operationeel" },
];

export const layerById = (id: string | undefined): SteeringLayer | undefined =>
  STEERING_LAYERS.find((laag) => laag.id === id);

export const layerByCode = (code: string | undefined | null): SteeringLayer | undefined =>
  STEERING_LAYERS.find((laag) => laag.code === code);

/** De lagen uit een lijst laagcodes, in de vaste volgorde. */
export function layersFromCodes(codes: readonly string[]): SteeringLayer[] {
  return STEERING_LAYERS.filter((laag) => codes.includes(laag.code));
}

/**
 * De lagen die deze gebruiker mag zien. `known` is onwaar zolang `GET /me`
 * nog niet terug is: dan weet het scherm het nog niet en stuurt het nergens heen.
 */
export function useSteeringLayers(): { layers: SteeringLayer[]; known: boolean } {
  const { account } = useAuth();
  const codes = account?.steeringLayers;
  return { layers: codes ? layersFromCodes(codes) : [], known: codes !== undefined };
}

/**
 * De laatst gebruikte laag in deze sessie, zodat het menu-item Dashboard daar
 * weer opent. Bewust in het geheugen en niet in de opslag van de browser: de
 * keuze staat in de URL (bouwspec §6).
 */
let lastLayer: SteeringLayer["id"] | null = null;

export const rememberLayer = (id: SteeringLayer["id"]): void => {
  lastLayer = id;
};

export const lastUsedLayer = (): SteeringLayer["id"] | null => lastLayer;
